import { Box, Divider, Grid, Typography } from '@mui/material';
import { BOOKING_CONTEXT } from '~/literals';
import { AgentFees, Offer } from '~/types';
import FlightCardItemOverview from './FlightCardItemOverview';
import { useTheme } from '@mui/material/styles';
import { Button, useMediaQuery } from '@mui/material';
import { getCurrencySign } from '~/modules/helpers';

export interface FlightCardProps {
  bookingContext: string;
  offer: Offer;
  onBookButtonClicked: (selectedOffers: Offer[]) => void;
  onDetailsButtonClicked?: (selectedOffer: Offer) => void;
  isSearchPage?: boolean;
  selectionOnly?: boolean;
}

function FlightCard({ offer: flight, bookingContext, onBookButtonClicked, onDetailsButtonClicked, isSearchPage = false, selectionOnly = false }: FlightCardProps) {
  const outbound = flight.itineraries.outbound;
  const inbound = flight.itineraries.inbound ? flight.itineraries.inbound : null;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const outboundFlightCard = inbound ? (
    <FlightCardItemOverview
      legGroup={outbound}
      passengerPricing={flight.passengerPricing}
      onDetailsButtonClicked={() => onDetailsButtonClicked && onDetailsButtonClicked(flight)}
      isSearchPage={isSearchPage}
    />
  ) : (
    <FlightCardItemOverview
      legGroup={outbound}
      passengerPricing={flight.passengerPricing}
      onDetailsButtonClicked={() => onDetailsButtonClicked && onDetailsButtonClicked(flight)}
      isSearchPage={isSearchPage}
    />
  );

  if (selectionOnly) {
    return (
      <Box
        component="article"
        aria-label="Flight itinerary option"
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          bgcolor: 'background.paper',
          boxShadow: '0 2px 8px rgba(16, 32, 51, 0.06)',
          overflow: 'hidden',
          transition: 'border-color 160ms ease, box-shadow 160ms ease',
          '&:hover, &:focus-within': {
            borderColor: 'primary.main',
            boxShadow: '0 4px 16px rgba(16, 32, 51, 0.10)',
          },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 180px' } }}>
          <Box sx={{ minWidth: 0, p: { xs: 2, sm: 3 } }}>
            <Typography variant="overline" component="h3" color="text.secondary" sx={{ mb: 1, fontWeight: 700, letterSpacing: 1 }}>
              Outbound flight
            </Typography>
            {outboundFlightCard}
            {inbound && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Typography variant="overline" component="h3" color="text.secondary" sx={{ mb: 1, fontWeight: 700, letterSpacing: 1 }}>
                  Return flight
                </Typography>
                <FlightCardItemOverview
                  legGroup={inbound}
                  passengerPricing={flight.passengerPricing}
                  onDetailsButtonClicked={() => onDetailsButtonClicked && onDetailsButtonClicked(flight)}
                  isSearchPage={isSearchPage}
                />
              </>
            )}
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 2, sm: 2.5 },
            bgcolor: 'action.hover',
            borderLeft: { xs: 0, md: '1px solid' },
            borderTop: { xs: '1px solid', md: 0 },
            borderColor: 'divider',
          }}>
            <Button fullWidth variant="contained" disableElevation sx={{ minHeight: 44, borderRadius: 2 }} onClick={() => onBookButtonClicked([flight])}>
              Select flight
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      borderRadius={5}
      bgcolor={'#fff'}
      mb={4}
      pl={1}
      pt={1}
      sx={{
        borderRadius: 5,
        borderTop: isMobile ? `2px solid ${theme.palette.primary.main}` : '', // Light blue on mobile only
        borderTopLeftRadius: { xs: 20, sm: 5 },
        borderTopRightRadius: { xs: 20, sm: 5 },
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={10}>
          <Box p={1} pt={2} pb={2}>
            {outboundFlightCard}
            {inbound && (
              <>
                <Divider sx={{ marginTop: 2, marginBottom: 3 }} />
                <FlightCardItemOverview
                  legGroup={inbound}
                  passengerPricing={flight.passengerPricing}
                  onDetailsButtonClicked={() => onDetailsButtonClicked && onDetailsButtonClicked(flight)}
                  isSearchPage={isSearchPage}
                />
              </>
            )}
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
          bgcolor={theme.palette.secondary.main}
          p={2}
          sx={isMobile
            ? {
              borderBottomLeftRadius: 20,
              borderBottomRightRadius: 20,
              width: '0%',
              maxWidth: '100vw',
              boxSizing: 'border-box',
              display: 'inline',
              marginLeft: 1,
            }
            : { borderRadius: 5 }
          }
        >
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            height="100%"
            color="#fff"
          >
            {!selectionOnly && <Box textAlign="center">
              <Typography variant="body2">
                <small>{flight.seatsAvailable} seats left at this price!</small>
              </Typography>
            </Box>}

            {!selectionOnly && <Box flexGrow={1} display="flex" justifyContent="center" alignItems="center">
              <Typography variant="h5">
                {getCurrencySign(flight.price.currency)}
                {getTotalPrice(flight.price.grandTotal, flight.agentFees, bookingContext)}
              </Typography>
            </Box>}

            <Box>
              <Button sx={{ width: '100%' }} variant="contained" onClick={() => onBookButtonClicked([flight])}>
                {selectionOnly ? 'Select flight' : bookingContext === BOOKING_CONTEXT.FLIGHT_ITINERARY ? 'Order Itinerary' : 'Book Now'}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

function getTotalPrice(gdsTotalPrice: string, agentFee: AgentFees, bookingContext: string): string {
  if (bookingContext === BOOKING_CONTEXT.FLIGHT_ITINERARY) return agentFee.total;

  return (parseFloat(agentFee.total) + parseFloat(gdsTotalPrice)).toFixed(2);
}

export default FlightCard;
